import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Slide,
  Alert,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../../firebase";

import InviteClientForm from "./InviteClientForm";
import CurrentClientsTable from "./CurrentClientsTable";
import InvitedClientsTable from "./InvitedClientsTable";
import AuditTrailTable from "./AuditTrailTable";
import MonthSelector from "./MonthSelector";
import AccountantAppBar from "./AccountantAppBar";
import AccountantSidebar from "./AccountantSidebar";
import DashboardSummary from "./DashboardSummary"; // ✅ Summary cards

import { useStateWithStorage } from "../../hooks/useStateWithStorage";

export default function AccountantDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useStateWithStorage(
    "accountantMonth",
    new Date().getMonth()
  );
  const [selectedYear, setSelectedYear] = useStateWithStorage(
    "accountantYear",
    new Date().getFullYear()
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("connected") === "quickbooks") {
      setShowBanner(true);

      const cleanedParams = new URLSearchParams(location.search);
      cleanedParams.delete("connected");
      navigate(
        {
          pathname: location.pathname,
          search: cleanedParams.toString(),
        },
        { replace: true }
      );

      const timer = setTimeout(() => {
        setShowBanner(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) return;

      const q = query(
        collection(db, "linkedClients"),
        where("accountantUid", "==", user.uid)
      );

      const unsubscribeClients = onSnapshot(q, (snapshot) => {
        const results = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setClients(results);
        setLoading(false);
      });

      return () => unsubscribeClients();
    });

    return () => unsubscribeAuth();
  }, []);

  const joinedClients = clients.filter((c) => !!c.clientUid);
  const invitedClients = clients.filter((c) => !c.clientUid);

  return (
    <Box sx={{ display: "flex" }}>
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Box p={4}>
          {showBanner && (
            <Slide in={showBanner} direction="down">
              <Alert severity="success" variant="filled" sx={{ mb: 2 }}>
                ✅ QuickBooks connected successfully!
              </Alert>
            </Slide>
          )}
          <DashboardSummary /> {/* ✅ Summary section */}
          <InviteClientForm />
          <AuditTrailTable
            accountantUid={auth.currentUser?.uid}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
          />
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <CurrentClientsTable
                clients={joinedClients}
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
              />
              <Divider sx={{ my: 3 }} />
              <InvitedClientsTable clients={invitedClients} />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
